import { useEffect, useRef, useState } from 'react'
import { Box, IconButton, Popover, Typography } from '@mui/material'
import { keyframes } from '@mui/system'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import palette from '../../../theme/palette'
import { customShadows } from '../../../theme/shadows'

// Shared building blocks for the About PACE pages (BD demo). Pure presentation: no API calls.

// Colours come from the admin theme (src/theme/palette.js) so this page follows the same palette as the rest of the panel.
// Kept as 6-digit hex so call sites can append an alpha suffix, e.g. `${C.primary}14`.
export const C = {
  primary: palette.primary.main,
  violet: palette.chart.violet[0],
  info: palette.info.main,
  teal: palette.chart.green[0],
  success: palette.success.main,
  warning: palette.warning.main,
  orange: palette.chart.red[0],
  error: palette.error.main,
  ink: palette.grey[800],
  muted: palette.grey[600],
  line: palette.grey[300],
  soft: palette.background.default,
}

// Light accents (warning, teal, orange) are too pale to read as text on white: use a darker shade for type.
const TEXT_SHADE = {
  [C.warning]: palette.warning.dark,
  [C.teal]: palette.success.dark,
  [C.orange]: palette.error.dark,
  [C.violet]: '#5C46D8',
}
export const textOn = (color) => TEXT_SHADE[color] || color

// Text colour for a solid fill of `color`: the light accents need dark type, the rest take white.
export const onColor = (color) => (color === C.warning || color === C.teal ? palette.grey[800] : '#fff')

export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`
// Ring colour comes from --pulse (set it on the element); falls back to the brand blue.
export const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 var(--pulse, rgba(32,101,209,.35)); }
  70%  { box-shadow: 0 0 0 10px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
`
export const dashFlow = keyframes`
  to { stroke-dashoffset: -24; }
`
export const growX = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`

const reduceMotion = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches

// True once the element has scrolled into view (fires once).
export function useInView(threshold = 0.2) {
  const ref = useRef(null)
  const [seen, setSeen] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || seen) return undefined
    if (typeof IntersectionObserver === 'undefined') { setSeen(true); return undefined }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect() } }, { threshold })
    io.observe(el)
    return () => io.disconnect()
  }, [seen, threshold])
  return [ref, seen]
}

// Fades + lifts its children in when scrolled into view.
export function Reveal({ children, delay = 0, sx }) {
  const [ref, seen] = useInView(0.12)
  return (
    <Box
      ref={ref}
      sx={{
        opacity: seen ? 1 : 0,
        animation: seen && !reduceMotion() ? `${fadeUp} .5s ease ${delay}ms both` : 'none',
        ...sx,
      }}
    >
      {children}
    </Box>
  )
}

// Number that counts up from 0 once visible.
export function CountUp({ to, decimals = 0, prefix = '', suffix = '', duration = 1400 }) {
  const [ref, seen] = useInView(0.3)
  const [val, setVal] = useState(0)
  useEffect(() => {
    if (!seen) return undefined
    if (reduceMotion()) { setVal(to); return undefined }
    let raf
    const t0 = performance.now()
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration)
      setVal(to * (1 - Math.pow(1 - p, 3)))
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [seen, to, duration])
  const txt = val.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
  return <span ref={ref}>{prefix}{txt}{suffix}</span>
}

// Small (i) button that opens a plain-language explanation.
export function InfoTip({ title, children, color = C.primary, size = 'small' }) {
  const [anchor, setAnchor] = useState(null)
  return (
    <>
      <IconButton
        size={size}
        aria-label={`More about ${title}`}
        onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget) }}
        sx={{ color: textOn(color), p: 0.4, '&:hover': { background: `${color}14` } }}
      >
        <InfoOutlinedIcon fontSize='small' />
      </IconButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { maxWidth: 360, p: 2, borderRadius: 2, borderTop: `3px solid ${color}`, boxShadow: customShadows.z16 } }}
      >
        <Typography variant='subtitle2' sx={{ color: textOn(color), mb: 0.75 }}>{title}</Typography>
        <Box sx={{ fontSize: 14, color: C.ink, lineHeight: 1.6, '& p': { m: 0, mb: 1 }, '& p:last-child': { mb: 0 } }}>{children}</Box>
      </Popover>
    </>
  )
}

export function SectionHead({ kicker, title, sub, color = C.primary, align = 'left' }) {
  return (
    <Box sx={{ mb: 3, textAlign: align }}>
      {kicker && (
        <Typography variant='overline' sx={{ display: 'block', color: textOn(color) }}>{kicker}</Typography>
      )}
      <Typography variant='h4' sx={{ fontSize: { xs: 22, md: 24 }, color: C.ink, mt: 0.25 }}>{title}</Typography>
      {sub && <Typography variant='body2' sx={{ color: C.muted, mt: 1, fontSize: 15, maxWidth: align === 'center' ? 760 : 'none', mx: align === 'center' ? 'auto' : 0 }}>{sub}</Typography>}
    </Box>
  )
}

export function Card({ children, sx, accent, hover = true, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        background: '#fff',
        borderRadius: 2,
        border: `1px solid ${palette.divider}`,
        p: 3,
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color .2s ease',
        cursor: onClick ? 'pointer' : 'default',
        ...(accent && { '&:before': { content: '""', position: 'absolute', inset: '0 0 auto 0', height: 3, background: accent } }),
        ...(hover && { '&:hover': { borderColor: palette.grey[400] } }),
        ...sx,
      }}
    >
      {children}
    </Box>
  )
}

export function IconBubble({ icon, color, size = 48 }) {
  return (
    <Box
      sx={{
        width: size, height: size, borderRadius: 1.5, display: 'grid', placeItems: 'center', color: textOn(color),
        background: `${color}1F`, flexShrink: 0,
        '& svg': { fontSize: size * 0.5 },
      }}
    >
      {icon}
    </Box>
  )
}

export function Pill({ children, color = C.primary, solid = false }) {
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.35, borderRadius: 99, fontSize: 12, fontWeight: 600,
        color: solid ? '#fff' : textOn(color), background: solid ? color : `${color}1F`, border: `1px solid ${color}33`, whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  )
}

// Footnote line under a proof point: where the number came from.
export function Source({ children }) {
  return <Typography variant='caption' sx={{ color: C.muted, display: 'block', mt: 1 }}>Source: {children}</Typography>
}
